import React, { useState, useEffect } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../ui/popover";
import { Label } from "../../ui/label";
import { useWorkflowStore } from "../../../store/useWorkflowStore";
import { useTargetServerConfigUsers, TargetUser } from "../../../lib/api";

/**
 * TargetAuthorSelector component
 *
 * Fetches users from SERVER_CONFIG endpoint with search functionality
 */
interface TargetAuthorSelectorProps {
  className?: string;
  label?: string;
}

export const TargetAuthorSelector: React.FC<TargetAuthorSelectorProps> = ({
  className,
  label = "Target Author"
}) => {
  const {
    targetAuthorId,
    setTargetAuthorId,
    targetFacilityId
  } = useWorkflowStore();

  // Local state for popover and search
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch users from SERVER_CONFIG endpoint with debounced search term
  const {
    data: users = [],
    isLoading,
    error
  } = useTargetServerConfigUsers(targetFacilityId, debouncedSearchTerm);

  // Clear search when facility changes
  useEffect(() => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setTargetAuthorId(null);
  }, [targetFacilityId, setTargetAuthorId]);

  // Determine if the combobox should be disabled
  const isDisabled = !targetFacilityId;

  // Find the selected user to display their name
  const selectedUser = users.find((user: TargetUser) => user.id === targetAuthorId);

  // Determine the placeholder text based on state
  const getPlaceholderText = () => {
    if (!targetFacilityId) return "Select target facility first";
    return "Select author...";
  };

  return (
    <div className={className}>
      <div className="grid w-full items-center gap-1.5">
        <Label htmlFor="target-author">{label}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id="target-author"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={isDisabled}
              className="w-full justify-between"
            >
              {selectedUser
                ? `${selectedUser.firstName} ${selectedUser.lastName} (${selectedUser.employeeId})`
                : getPlaceholderText()}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search by name, employee ID, or email..."
                className="h-9"
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
              <CommandList>
                <CommandEmpty>
                  {isLoading
                    ? "Loading authors..."
                    : error
                      ? `Error: ${error instanceof Error ? error.message : "Failed to load authors"}`
                      : searchTerm
                        ? `No authors found matching "${searchTerm}"`
                        : "No authors available"}
                </CommandEmpty>
                <CommandGroup>
                  {users.map((user: TargetUser) => (
                    <CommandItem
                      key={user.id}
                      value={user.id}
                      onSelect={(currentValue) => {
                        setTargetAuthorId(currentValue === targetAuthorId ? null : currentValue);
                        setOpen(false);
                      }}
                    >
                      {user.firstName} {user.lastName} ({user.employeeId})
                      <Check
                        className={cn(
                          "ml-auto h-4 w-4",
                          targetAuthorId === user.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default TargetAuthorSelector;